import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Axios from "../../services/AxiosInstance";

// Create Employee: Admin-side employee creation under ONE selected HR.
// Step 1 selects the HR (GET api/admin/hr-list, same source as HrList);
// step 2 submits the Partner employee-creation contract
// (POST api/admin/hr-list/:hrId/employees): same field names, same limits
// and same messages as VedMGHRMSAPI createEmployeeSchema. created_by and
// business are stamped server-side from the validated HR, never from the
// client — the Admin equivalent of Partner stamping them from the HR JWT.
const CreateEmployee = () => {
  const navigate = useNavigate();
  const [hrs, setHrs] = useState([]);
  const [loadingHrs, setLoadingHrs] = useState(false);
  const [hrError, setHrError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selectedHrId, setSelectedHrId] = useState("");

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobile: "",
    department: "",
    designation: "",
    status: "Active",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [toast, setToast] = useState("");
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const fetchHrs = useCallback(async () => {
    setLoadingHrs(true);
    setHrError("");
    try {
      const params = new URLSearchParams({
        pageNo: "1",
        perPage: "100",
      });
      if (appliedSearch) params.set("search", appliedSearch);
      const res = await Axios.get(`api/admin/hr-list?${params.toString()}`);
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setHrs(list);
    } catch (err) {
      setHrs([]);
      setHrError("Failed to load HR users. Please retry.");
    } finally {
      setLoadingHrs(false);
    }
  }, [appliedSearch]);

  useEffect(() => {
    fetchHrs();
  }, [fetchHrs]);

  const selectedHr = hrs.find((h) => h.id === selectedHrId) || null;

  const handleHrChange = (e) => {
    setSelectedHrId(e.target.value);
    setServerError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Client-side mirror of the server create contract
  // (hrListService.validateHrEmployeeCreateBody) so obvious mistakes never
  // hit the API.
  const validate = () => {
    const errs = {};
    if (!form.full_name.trim())
      errs.full_name = '"Full Name" is required';
    else if (form.full_name.trim().length < 2)
      errs.full_name =
        '"Full Name" length must be at least 2 characters long';
    else if (form.full_name.trim().length > 200)
      errs.full_name =
        '"Full Name" length must be less than or equal to 200 characters long';
    if (!form.email.trim()) errs.email = '"Email" is required';
    else if (form.email.trim().length > 200)
      errs.email =
        '"Email" length must be less than or equal to 200 characters long';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = '"Email" must be a valid email';
    if (!form.password) errs.password = '"Password" is required';
    else if (form.password.length < 8)
      errs.password =
        '"Password" length must be at least 8 characters long';
    else if (form.password.length > 20)
      errs.password =
        '"Password" length must be less than or equal to 20 characters long';
    if (form.confirmPassword !== form.password)
      errs.confirmPassword = "Passwords must match";
    if (
      form.mobile.trim() &&
      !/^[0-9+ -]{10,20}$/.test(form.mobile.trim())
    )
      errs.mobile = '"Mobile" must be a valid mobile number';
    if (form.department.trim().length > 200)
      errs.department =
        '"Department" length must be less than or equal to 200 characters long';
    if (form.designation.trim().length > 200)
      errs.designation =
        '"Designation" length must be less than or equal to 200 characters long';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedHr) {
      setServerError("Please select an HR first.");
      return;
    }
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    setServerError("");
    try {
      await Axios.post(
        `api/admin/hr-list/${encodeURIComponent(selectedHr.id)}/employees`,
        {
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          password: form.password,
          mobile: form.mobile.trim(),
          department: form.department.trim(),
          designation: form.designation.trim(),
          status: form.status,
        }
      );
      showToast("Employee created successfully");
      setTimeout(
        () => navigate(`/hr-list/${encodeURIComponent(selectedHr.id)}`),
        600
      );
    } catch (err) {
      setServerError(
        err?.response?.data?.message ||
          "Failed to create employee. Please retry."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const clearForm = () => {
    setForm({
      full_name: "",
      email: "",
      password: "",
      confirmPassword: "",
      mobile: "",
      department: "",
      designation: "",
      status: "Active",
    });
    setErrors({});
    setServerError("");
  };

  return (
    <div
      className="container mt-4"
      style={{ maxWidth: "1280px", margin: "0 auto" }}
    >
      <div className="text-center mb-4">
        <h2 style={{ color: "#10283c", fontWeight: 800 }}>
          Create Employee
        </h2>
        <p className="text-muted mb-0">
          Select an HR, then enter the employee details
        </p>
      </div>

      <div className="card mb-4">
        <div
          className="card-header text-white d-flex justify-content-between align-items-center"
          style={{ backgroundColor: "#10283c" }}
        >
          <span style={{ fontSize: "20px", fontWeight: "600" }}>
            Select HR
          </span>
          <span style={{ fontSize: "14px", opacity: 0.85 }}>
            {hrs.length} HR{hrs.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="p-4">
          {hrError && (
            <div className="alert alert-danger text-center">{hrError}</div>
          )}
          <div className="row g-3 hr-list-filters">
            <div className="col-md-5">
              <input
                type="text"
                className="form-control"
                placeholder="Search HR name, email or id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setAppliedSearch(search);
                }}
              />
            </div>
            <div className="col-md-4 d-flex gap-2">
              <button
                className="btn btn-primary flex-fill"
                onClick={() => setAppliedSearch(search)}
                disabled={loadingHrs}
              >
                Search
              </button>
              <button
                className="btn btn-secondary flex-fill"
                onClick={() => {
                  setSearch("");
                  setAppliedSearch("");
                }}
                disabled={loadingHrs}
              >
                Clear
              </button>
            </div>
          </div>
          <div className="row g-3 hr-list-filters mt-1">
            <div className="col-md-8">
              <label className="form-label" htmlFor="create-emp-hr">
                HR
              </label>
              <select
                id="create-emp-hr"
                className="form-control"
                value={selectedHrId}
                onChange={handleHrChange}
                disabled={loadingHrs}
              >
                <option value="">
                  {loadingHrs ? "Loading HR users..." : "Select HR"}
                </option>
                {hrs.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name} — {h.business_name} ({h.id})
                  </option>
                ))}
              </select>
            </div>
          </div>
          {selectedHr && (
            <div className="alert alert-info mt-3 mb-0">
              Creating employee under <strong>{selectedHr.name}</strong>
              {selectedHr.business_name
                ? ` (${selectedHr.business_name})`
                : ""}{" "}
              — {selectedHr.employeeCount ?? 0} existing employee
              {(selectedHr.employeeCount ?? 0) === 1 ? "" : "s"}.
            </div>
          )}
        </div>
      </div>

      {selectedHr && (
        <div className="card mb-4">
          <div
            className="card-header text-white"
            style={{ backgroundColor: "#10283c" }}
          >
            <span style={{ fontSize: "20px", fontWeight: "600" }}>
              Employee Details
            </span>
          </div>
          <div className="p-4">
            {serverError && (
              <div className="alert alert-danger text-center">
                {serverError}
              </div>
            )}
            <form onSubmit={handleSubmit} noValidate>
              <div className="row g-3 hr-list-filters">
                <div className="col-md-6">
                  <label className="form-label" htmlFor="create-emp-name">
                    Full Name
                  </label>
                  <input
                    id="create-emp-name"
                    name="full_name"
                    type="text"
                    className="form-control"
                    placeholder="Enter employee full name"
                    value={form.full_name}
                    onChange={handleChange}
                  />
                  {errors.full_name && (
                    <div className="text-danger mt-1">{errors.full_name}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label" htmlFor="create-emp-email">
                    Email
                  </label>
                  <input
                    id="create-emp-email"
                    name="email"
                    type="email"
                    className="form-control"
                    placeholder="Enter employee email"
                    value={form.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <div className="text-danger mt-1">{errors.email}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label" htmlFor="create-emp-password">
                    Password
                  </label>
                  <input
                    id="create-emp-password"
                    name="password"
                    type="password"
                    className="form-control"
                    placeholder="8-20 characters"
                    value={form.password}
                    onChange={handleChange}
                  />
                  {errors.password && (
                    <div className="text-danger mt-1">{errors.password}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label" htmlFor="create-emp-confirm">
                    Confirm Password
                  </label>
                  <input
                    id="create-emp-confirm"
                    name="confirmPassword"
                    type="password"
                    className="form-control"
                    placeholder="Re-enter password"
                    value={form.confirmPassword}
                    onChange={handleChange}
                  />
                  {errors.confirmPassword && (
                    <div className="text-danger mt-1">
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label" htmlFor="create-emp-mobile">
                    Mobile
                  </label>
                  <input
                    id="create-emp-mobile"
                    name="mobile"
                    type="text"
                    className="form-control"
                    placeholder="Mobile number (optional)"
                    value={form.mobile}
                    onChange={handleChange}
                  />
                  {errors.mobile && (
                    <div className="text-danger mt-1">{errors.mobile}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label" htmlFor="create-emp-dept">
                    Department
                  </label>
                  <input
                    id="create-emp-dept"
                    name="department"
                    type="text"
                    className="form-control"
                    placeholder="Department (optional)"
                    value={form.department}
                    onChange={handleChange}
                  />
                  {errors.department && (
                    <div className="text-danger mt-1">{errors.department}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label" htmlFor="create-emp-desig">
                    Designation
                  </label>
                  <input
                    id="create-emp-desig"
                    name="designation"
                    type="text"
                    className="form-control"
                    placeholder="Designation (optional)"
                    value={form.designation}
                    onChange={handleChange}
                  />
                  {errors.designation && (
                    <div className="text-danger mt-1">
                      {errors.designation}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label" htmlFor="create-emp-status">
                    Status
                  </label>
                  <select
                    id="create-emp-status"
                    name="status"
                    className="form-control"
                    value={form.status}
                    onChange={handleChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div className="col-12 d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? "Creating..." : "Create Employee"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={clearForm}
                    disabled={submitting}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "65px",
            right: "20px",
            background: "#28a745",
            color: "white",
            padding: "12px 20px",
            borderRadius: "6px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            zIndex: 2000,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default CreateEmployee;
